import { Capacitor, registerPlugin } from '@capacitor/core';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

export const isApple = Capacitor.getPlatform() === 'ios';
const store = registerPlugin('ApplePurchases');
export const APPLE_PRODUCTS = { monthly: 'uk.org.taptap.pro.monthly', annual: 'uk.org.taptap.pro.annual' };
const inFlight = new Map();
const assertUser = uid => {
  if (!uid || auth.currentUser?.uid !== uid) throw new Error('Please sign in to the account used for this purchase.');
};
const context = async (purchase = false, plan) => {
  const uid = auth.currentUser?.uid;
  assertUser(uid);
  const { data } = await httpsCallable(functions, 'prepareApplePurchase')({ purchase, ...(plan ? { plan } : {}) });
  assertUser(uid);
  return { uid, token: data.appAccountToken };
};
const sync = async (transaction, account) => {
  assertUser(account.uid);
  if (transaction.appAccountToken !== account.token) {
    throw new Error('This Apple purchase belongs to another TapTap account. Sign in to that account to restore it.');
  }
  const key = `${account.uid}:${transaction.transactionId}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const promise = (async () => {
    const { data } = await httpsCallable(functions, 'syncApplePurchase')({ signedTransaction: transaction.signedTransaction });
    assertUser(account.uid);
    await store.finish({ transactionId: transaction.transactionId });
    return data;
  })();
  inFlight.set(key, promise);
  try { return await promise; } finally { inFlight.delete(key); }
};
export const appleProducts = async () => {
  const { products } = await store.products();
  return Object.fromEntries(Object.entries(APPLE_PRODUCTS).map(([plan, id]) => [plan, products.find(item => item.id === id)]));
};
export const purchaseApple = async plan => {
  if (!APPLE_PRODUCTS[plan]) throw new Error('Choose a subscription plan.');
  const account = await context(true, plan);
  const result = await store.purchase({ productId: APPLE_PRODUCTS[plan], appAccountToken: account.token });
  if (result.status === 'purchased') {
    try { await sync(result.transaction, account); }
    catch (error) {
      throw new Error(`Your payment may have completed. Use Restore Purchases before trying another purchase. ${error.message || ''}`.trim());
    }
  }
  return result;
};
export const restoreApple = async () => {
  const account = await context();
  const { transactions } = await store.restore();
  const owned = transactions.filter(item => item.appAccountToken === account.token);
  if (!owned.length && transactions.length) throw new Error('Sign in to the TapTap account originally used for this Apple purchase.');
  let active = false;
  for (const transaction of owned) active = (await sync(transaction, account)).active || active;
  return { active };
};
export const manageApple = () => store.manage();
export const openWebBilling = url => store.openBilling({ url });

// Start at login, recover unfinished payments and reconcile on foreground/network
// recovery. Listener cleanup also handles a sign-out while registration is pending.
export const watchApplePurchases = uid => {
  let stopped = false, listener, refreshing = false;
  const refresh = async () => {
    if (stopped || refreshing || document.visibilityState === 'hidden' || auth.currentUser?.uid !== uid) return;
    refreshing = true;
    try {
      const { transactions } = await store.transactions();
      if (!transactions.length) return;
      const account = await context();
      for (const transaction of transactions) {
        if (stopped) return;
        if (transaction.appAccountToken === account.token) await sync(transaction, account);
      }
    } catch { /* Offline/unconfigured: keep unfinished transactions for retry/Restore. */ }
    finally { refreshing = false; }
  };
  store.addListener('transactionUpdated', refresh).then(handle => {
    if (stopped) handle.remove(); else listener = handle;
  }).catch(() => {});
  window.addEventListener('online', refresh);
  document.addEventListener('visibilitychange', refresh);
  refresh();
  return () => {
    stopped = true;
    listener?.remove();
    window.removeEventListener('online', refresh);
    document.removeEventListener('visibilitychange', refresh);
  };
};
